'use client'

import * as React from 'react'
import Script from 'next/script'

// Shared Cloudflare Turnstile hook + client widget. Wraps the
// explicit-render API so multiple forms on the page can each manage
// their own widget without colliding. Falls back to a no-op when no
// NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY is configured (so local
// dev / preview without secrets still works).

const TURNSTILE_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string
          callback: (token: string) => void
          'error-callback'?: () => void
          'expired-callback'?: () => void
          theme?: 'auto' | 'light' | 'dark'
          size?: 'normal' | 'compact' | 'flexible'
          appearance?: 'always' | 'execute' | 'interaction-only'
        },
      ) => string
      reset: (widgetId?: string) => void
      remove: (widgetId?: string) => void
    }
  }
}

export function useTurnstileSiteKey(): string | undefined {
  return process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY
}

type WidgetProps = {
  /** Optional size — 'compact' renders a smaller widget for tight rows. */
  size?: 'normal' | 'compact' | 'flexible'
  /** 'interaction-only' (default) keeps the widget invisible unless Cloudflare
   *  actually needs the visitor to interact, so the vast majority of
   *  submissions never show any Cloudflare chrome at all. */
  appearance?: 'always' | 'execute' | 'interaction-only'
  /** Called with the verification token whenever the user passes the challenge. */
  onToken: (token: string) => void
  /** Called when the existing token expires or errors out. */
  onExpire?: () => void
  /** Called when the challenge can't load — site key not allowlisted,
   *  region-blocked, network down, etc. Forms should switch to a
   *  fallback path (e.g. mailto) when this fires. */
  onError?: () => void
  className?: string
}

export function TurnstileWidget({ size = 'normal', appearance = 'interaction-only', onToken, onExpire, onError, className }: WidgetProps) {
  const siteKey = useTurnstileSiteKey()
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const widgetIdRef  = React.useRef<string | null>(null)
  const [scriptReady, setScriptReady] = React.useState<boolean>(
    typeof window !== 'undefined' && Boolean(window.turnstile),
  )

  // Stash the callbacks in refs so we can render the widget exactly
  // once per mount. Without this, every parent re-render (e.g. after
  // setTurnstileToken) creates new function references, the effect
  // tears the widget down, re-mounts it, and the user sees the
  // challenge again immediately after solving it. Classic React +
  // imperative-widget collision.
  const onTokenRef  = React.useRef(onToken)
  const onExpireRef = React.useRef(onExpire)
  const onErrorRef  = React.useRef(onError)
  React.useEffect(() => { onTokenRef.current  = onToken  }, [onToken])
  React.useEffect(() => { onExpireRef.current = onExpire }, [onExpire])
  React.useEffect(() => { onErrorRef.current  = onError  }, [onError])

  React.useEffect(() => {
    if (!siteKey || !scriptReady || widgetIdRef.current || !containerRef.current || !window.turnstile) return
    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: siteKey,
      // Hardcoded 'light' rather than 'auto': 'auto' follows the visitor's
      // OS color-scheme, which produced a dark Cloudflare box dropped into
      // our light-themed cards. All the surfaces this widget renders on
      // are light, so match that instead of the OS preference.
      theme: 'light',
      size,
      appearance,
      callback:           (token) => onTokenRef.current(token),
      'expired-callback': () => { onExpireRef.current?.(); onTokenRef.current('') },
      'error-callback':   () => { onErrorRef.current?.();  onTokenRef.current('') },
    })
    return () => {
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current) } catch { /* ignore */ }
        widgetIdRef.current = null
      }
    }
    // Intentionally exhaustive: only re-render the widget when the
    // siteKey, size, or appearance actually changes, or the script becomes ready.
  }, [siteKey, scriptReady, size, appearance])

  if (!siteKey) return null

  return (
    <>
      <Script
        src={TURNSTILE_SCRIPT}
        strategy="lazyOnload"
        onLoad={() => setScriptReady(true)}
        onError={() => onErrorRef.current?.()}
      />
      <div ref={containerRef} className={className} />
    </>
  )
}
